import React, { useMemo, useState } from "react";
import { Modal, Button, ComboField } from "@truevault/aquilo";
import type { ComboFieldItem } from "@truevault/aquilo";
import {
  InformationCircleIcon,
  MagnifyingGlassIcon,
  ArrowLeftIcon,
} from "@heroicons/react/20/solid";
import { Combobox } from "@headlessui/react";
import { CircularProgress } from "@mui/material";
import { VendorDto } from "../../../../common/service/server/dto/VendorDto";
import {
  useDataRecipientsCatalog,
  useDataSourceCatalog,
} from "../../../../common/hooks/useVendors";
import { DataRecipientType, OrganizationPublicId } from "../../../../common/service/server/Types";
import { PuzzlePieceIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { DATA_STORAGE_LOCATION_CATEGORY } from "../../../util/vendors";
import { vendorPopularityRank } from "./vendorPopularity";
import { CONTRACTOR_CATEGORY } from "../../../types/Vendor";
import { THIRD_PARTY_CATEGORIES } from "../../get-compliant/data-recipients/ThirdPartyRecipientsPage";
import { validateNotIn } from "../../../util/validation";

// There are 4 types of vendors to add:
//   1. existing vendor from vendor database: will have `id`, `name`
//   2. new custom vendor: will have `id` = `_NEW_VENDOR`, `name`, `category`, `isInternalStorage`
//   3. new internal data storage vendor: will have `id` = `_NEW_VENDOR`, `name`, `category`, isInternalStorage`
//   4. new contractor vendor: will have `id` = `_NEW_VENDOR`, `name`, `category`
// Note that this object type is used for two purposes: (1) for representing vendor options to display in the autocomplete and (2) to keep track of which vendors are selected (including new custom vendors to be created) - this also means that some fields are only relevant in one context but not the other, e.g. `isNewVendor` is only used for context (2) and logoUrl is only used for context (1)
export interface SelectedVendor {
  id?: string;
  name?: string;
  vendorKey?: string;
  logoUrl?: string;
  category?: string;
  isCustom?: boolean; // used to determine whether to show puzzle icon in selected vendors list
  isInternalStorage?: boolean;
  isNewVendor?: boolean;
  newVendorType?: DataRecipientType;
  isFromDataSourceCatalog?: boolean;
}

type VendorCatalogOption = VendorDto & Pick<SelectedVendor, "isFromDataSourceCatalog">;

// Special vendor options that appear in search results
const CUSTOM_VENDOR_OPTION = {
  id: "_CUSTOM_VENDOR_OPTION",
  name: "Custom Vendor / Internal Data Storage",
  logoUrl: "",
  isSpecial: true,
  isCustom: true,
};

const CONTRACTOR_OPTION = {
  id: "_CONTRACTOR_OPTION",
  name: "Contractor / Agency",
  logoUrl: "",
  isSpecial: true,
  isCustom: false,
};

type ModalView = "main" | "custom-vendor-details" | "contractor-details";

const EMPTY_REUSABLE_VENDOR_IDS = new Set<string>();

export function AddVendorModal({
  organizationId,
  canAddVendor,
  reusableDataSourceVendorIds = EMPTY_REUSABLE_VENDOR_IDS,
  includeReusableDataSourceCatalog = false,
  existingContractorNames = [],
  initialSelectedVendors,
  onClose,
  onDone,
  initialCategory,
  infoTitle,
  infoBody,
}: {
  organizationId: OrganizationPublicId;
  canAddVendor: (vendorId: string) => boolean;
  reusableDataSourceVendorIds?: Set<string>;
  includeReusableDataSourceCatalog?: boolean;
  existingContractorNames?: string[];
  initialSelectedVendors?: SelectedVendor[];
  onClose: () => void;
  onDone: (selectedVendors: SelectedVendor[]) => void;
  // When set, the vendor list is scoped to this category (opened from an empty category table).
  initialCategory?: string;
  infoTitle?: string;
  infoBody?: string;
}) {
  const [vendorCatalog] = useDataRecipientsCatalog(
    organizationId,
    /* includeDataStorageLocations = */ true,
  );
  const [dataSourceCatalog] = useDataSourceCatalog(
    organizationId,
    /* includeDataStorageLocations = */ true,
  );
  const [selectedVendors, setSelectedVendors] = useState<SelectedVendor[]>(
    initialSelectedVendors ?? [],
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [currentView, setCurrentView] = useState<ModalView>("main");
  const [isLoading, setIsLoading] = useState(false);
  // The active category scope. Starts as the category the modal was opened from (if any); "Show me vendors from
  // all categories" clears it, turning this into the regular unscoped Add Vendor modal.
  const [categoryScope, setCategoryScope] = useState<string | undefined>(initialCategory);

  const autocompleteCatalog = useMemo(() => {
    const vendorsById = new Map<string, VendorCatalogOption>(
      vendorCatalog.map((vendor) => [vendor.id, vendor]),
    );

    if (includeReusableDataSourceCatalog) {
      // HR can reuse Consumer-only custom data sources that recipient catalog omits.
      dataSourceCatalog
        .filter((vendor) => reusableDataSourceVendorIds.has(vendor.id))
        .forEach((vendor) => {
          if (!vendorsById.has(vendor.id)) {
            vendorsById.set(vendor.id, { ...vendor, isFromDataSourceCatalog: true });
          }
        });
    }

    return Array.from(vendorsById.values());
  }, [
    dataSourceCatalog,
    includeReusableDataSourceCatalog,
    reusableDataSourceVendorIds,
    vendorCatalog,
  ]);

  const selectedVendorIds = useMemo(
    () => new Set(selectedVendors.map((v) => v.id)),
    [selectedVendors],
  );

  // Filter vendors based on the search query, scoped to the active category when one is set. With an empty query
  // this returns the first vendors of the (scoped) catalog, so the dropdown has content as soon as the field is
  // focused — before the user types anything.
  const filteredVendors = useMemo(() => {
    const lowerQuery = searchQuery.toLowerCase();
    const matchingVendors = autocompleteCatalog
      .filter((vendor) => {
        if (!canAddVendor(vendor.id)) {
          return false;
        }
        if (categoryScope && vendor.category !== categoryScope) {
          return false;
        }
        return lowerQuery.length < 1 || vendor.name.toLowerCase().includes(lowerQuery);
      })
      .sort(
        (a, b) =>
          vendorPopularityRank(a.name) - vendorPopularityRank(b.name) ||
          a.name.localeCompare(b.name),
      )
      .slice(0, 8);

    // Add special options at the end
    return [...matchingVendors, CUSTOM_VENDOR_OPTION, CONTRACTOR_OPTION];
  }, [autocompleteCatalog, canAddVendor, categoryScope, searchQuery]);

  const handleSelectVendor = (vendor: VendorCatalogOption | typeof CUSTOM_VENDOR_OPTION) => {
    if (!vendor) return;

    if (vendor.id === "_CUSTOM_VENDOR_OPTION") {
      setCurrentView("custom-vendor-details");
      return;
    }

    if (vendor.id === "_CONTRACTOR_OPTION") {
      setCurrentView("contractor-details");
      return;
    }

    // Check if vendor is already selected
    if (selectedVendorIds.has(vendor.id)) {
      return;
    }

    setSelectedVendors([
      ...selectedVendors,
      {
        id: vendor.id,
        // @ts-ignore guaranteed to have vendorKey since we already checked for _CUSTOM_VENDOR_OPTION and _CONTRACTOR_OPTION
        vendorKey: vendor.vendorKey ?? undefined,
        isNewVendor: false,
        name: vendor.name,
        logoUrl: vendor.logoUrl,
        isFromDataSourceCatalog:
          "isFromDataSourceCatalog" in vendor && vendor.isFromDataSourceCatalog,
      },
    ]);
    setSearchQuery("");
  };

  const handleAddCustomVendor = (customVendor: SelectedVendor) => {
    setSelectedVendors((prev) => [...prev, customVendor]);
    setSearchQuery("");
    setCurrentView("main");
  };

  const handleAddContractor = (contractor: SelectedVendor) => {
    setSelectedVendors((prev) => [...prev, contractor]);
    setSearchQuery("");
    setCurrentView("main");
  };

  const handleRemoveVendor = (selectedVendorIdx: number) => {
    setSelectedVendors((prev) => prev.filter((_, idx) => idx !== selectedVendorIdx));
  };

  const handleDone = async () => {
    setIsLoading(true);
    try {
      await onDone(selectedVendors);
    } finally {
      setIsLoading(false);
    }
  };

  const existingCategories = useMemo(() => {
    return new Set(vendorCatalog.map((v) => v.category).filter((x) => x));
  }, [vendorCatalog]);

  const contractorNamesInUse = useMemo(() => {
    const selectedNames = selectedVendors
      .filter((v) => v.newVendorType === "contractor" && v.name)
      .map((v) => v.name as string);
    return [...existingContractorNames, ...selectedNames];
  }, [existingContractorNames, selectedVendors]);

  if (currentView === "custom-vendor-details") {
    return (
      <CustomVendorDetailsModal
        initialVendorName={searchQuery}
        initialCategory={categoryScope}
        existingCategories={new Set([...existingCategories, ...THIRD_PARTY_CATEGORIES])}
        onBack={() => setCurrentView("main")}
        onCancel={onClose}
        onDone={handleAddCustomVendor}
      />
    );
  }

  if (currentView === "contractor-details") {
    return (
      <ContractorDetailsModal
        initialVendorName={searchQuery}
        existingCategories={existingCategories}
        existingContractorNames={contractorNamesInUse}
        onBack={() => setCurrentView("main")}
        onCancel={onClose}
        onDone={handleAddContractor}
      />
    );
  }

  return (
    <Modal
      title="Add New Vendor(s)"
      alignText="left"
      alignButton="right"
      firstButton="Cancel"
      firstButtonDisabled={isLoading}
      onFirstButtonClick={onClose}
      secondButton="Done"
      secondButtonColor="primary"
      secondButtonDisabled={isLoading}
      onSecondButtonClick={handleDone}
      showOverlay
      closeButton
      onClose={isLoading ? undefined : onClose}
      maxPanelWidth="max-w-[600px]"
      contentContainerClasses="w-full"
    >
      <div className="relative">
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-50 z-10 flex items-center justify-center rounded-md">
            <CircularProgress size="3em" />
          </div>
        )}

        <div className="space-y-4">
          {/* Info box */}
          <div className="flex gap-3 p-4 bg-blue-50 rounded-md">
            <div className="flex-shrink-0">
              <InformationCircleIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div className="text-sm">
              <p className="text-blue-700 font-medium">
                {infoTitle ??
                  "Add a vendor, third party (such as Co-Marketing Partner, Data Buyer, Business Partner, Affiliate or other) or contractor (like a Marketing Agency)."}
              </p>
              <p className="text-blue-700 mt-1">
                {infoBody ??
                  "Do not include any HR vendors or employment related vendors. Those will be listed in your HR Data Map."}
              </p>
            </div>
          </div>

          {/* Vendor Name Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name</label>
            {categoryScope && (
              <div className="mb-2">
                <p className="text-sm text-neutral-600">
                  Showing <span className="font-medium text-neutral-800">{categoryScope}</span>{" "}
                  vendors. Click the field to browse them or search by name.
                </p>
                <button
                  type="button"
                  onClick={() => setCategoryScope(undefined)}
                  className="block text-sm font-medium cursor-pointer hover:underline mt-1"
                  style={{ color: "#5331C6" }}
                >
                  Show me vendors from all categories
                </button>
              </div>
            )}
            <VendorAutocomplete
              vendors={filteredVendors}
              selectedVendorIds={selectedVendorIds}
              query={searchQuery}
              onQueryChange={setSearchQuery}
              onSelect={handleSelectVendor}
              isDropdownOpen={filteredVendors.length > 0}
              selectedVendorsCount={selectedVendors.length}
            />
          </div>

          {/* Selected Vendors List */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Vendor(s) Added</label>
            {selectedVendors.length === 0 ? (
              <div
                className={clsx(
                  "flex flex-col items-center justify-center gap-3 px-6 py-10",
                  "min-h-[200px]", // 200px calculated so that the autocomplete dropdown won't go beyond the modal
                )}
              >
                <p className="text-sm text-gray-500">You haven't added any vendors yet</p>
              </div>
            ) : (
              <div className="min-h-[200px]">
                <div className="space-y-2">
                  {selectedVendors.map((vendor, idx) => (
                    <SelectedVendorRow
                      key={vendor.id || `${vendor.name}-${idx}`}
                      vendor={vendor}
                      onRemove={() => handleRemoveVendor(idx)}
                    />
                  ))}
                </div>
                <div className="mt-3 text-center text-sm text-neutral-600 font-medium">
                  Nice! You can add up to 20 vendors
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

// Height for exactly 4 dropdown entries: 4 * 60px (per entry) + 24px (container padding) + 4px (mt-1 margin)
// const DROPDOWN_HEIGHT_FOR_4_ENTRIES = 268;
// Height per selected vendor row (~42px row + 8px gap from space-y-2)
// const SELECTED_VENDOR_ROW_HEIGHT = 50;
// Header height for the "Vendor(s) Added" section (label + mb-2 + space-y-4 gap from parent)
// const SELECTED_VENDORS_HEADER_HEIGHT = 44;
// Height of the empty state (dashed border container with padding and text)
// const EMPTY_VENDORS_SECTION_HEIGHT = 144;

interface VendorAutocompleteProps {
  vendors: (VendorDto | typeof CUSTOM_VENDOR_OPTION)[];
  selectedVendorIds: Set<string>;
  query: string;
  onQueryChange: (query: string) => void;
  onSelect: (vendor: VendorDto | typeof CUSTOM_VENDOR_OPTION) => void;
  isDropdownOpen: boolean;
  selectedVendorsCount: number;
}

function VendorAutocomplete({
  vendors,
  selectedVendorIds,
  query,
  onQueryChange,
  onSelect,
}: // isDropdownOpen,
// selectedVendorsCount,
VendorAutocompleteProps) {
  // Calculate spacer height accounting for space already taken by selected vendors section
  // const selectedVendorsHeight =
  //   selectedVendorsCount > 0
  //     ? SELECTED_VENDORS_HEADER_HEIGHT + selectedVendorsCount * SELECTED_VENDOR_ROW_HEIGHT
  //     : SELECTED_VENDORS_HEADER_HEIGHT + EMPTY_VENDORS_SECTION_HEIGHT;
  // const spacerHeight = Math.max(0, DROPDOWN_HEIGHT_FOR_4_ENTRIES - selectedVendorsHeight);

  // Show the dropdown as soon as the field is focused (before any typing), not only once HeadlessUI opens it on
  // input. The options list is rendered `static` while focused; mousedown on it is prevented so clicking an
  // option doesn't blur the input (which would unmount the list before the click registers).
  const [isFocused, setIsFocused] = useState(false);

  return (
    <Combobox onChange={onSelect} value={null}>
      <div className="relative">
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <Combobox.Input
            className="block w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            placeholder="Search over 2,500 vendors or add a custom vendor"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            autoComplete="off"
          />
        </div>

        {isFocused && vendors.length > 0 && (
          <Combobox.Options
            static
            onMouseDown={(e) => e.preventDefault()}
            className="absolute z-10 mt-1 max-h-[264px] w-full overflow-y-auto p-3 bg-white rounded-md"
            style={{
              scrollbarWidth: "none",
              boxShadow: "0px 2px 12px 0px rgba(0, 0, 0, 0.25)",
            }}
          >
            {vendors.map((vendor) => {
              const isAdded = selectedVendorIds.has(vendor.id);
              const isSpecial = "isSpecial" in vendor && vendor.isSpecial;
              const isCustomVendorOption = vendor.id === "_CUSTOM_VENDOR_OPTION";
              const isContractorOption = vendor.id === "_CONTRACTOR_OPTION";

              return (
                <Combobox.Option key={vendor.id} value={vendor} disabled={isAdded} as="div">
                  {({ active }) => (
                    <div
                      className={clsx(
                        "flex items-center gap-2 mb-1 px-4 py-4 border border-solid rounded cursor-pointer",
                        active && !isAdded ? "border-neutral-900" : "border-neutral-200",
                        isAdded ? "opacity-50 cursor-not-allowed" : "",
                        (isCustomVendorOption || isContractorOption) && active
                          ? "border-primary-500"
                          : "",
                      )}
                    >
                      {/* Logo / Icon */}
                      {isSpecial ? (
                        <div className="h-6 w-6 flex-shrink-0 flex items-center justify-center">
                          <PuzzlePieceIcon className="h-5 w-5 text-gray-500" />
                        </div>
                      ) : vendor.logoUrl ? (
                        <img
                          src={vendor.logoUrl}
                          alt={vendor.name}
                          className="h-6 w-6 flex-shrink-0"
                        />
                      ) : (
                        <div className="h-6 w-6 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs text-gray-500">
                            {vendor.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}

                      {/* Name */}
                      <span
                        className={clsx(
                          "text-sm text-gray-900",
                          active && !isAdded ? "font-medium" : "",
                        )}
                      >
                        {vendor.name}
                      </span>

                      {/* Added badge or Add button for custom vendor/contractor */}
                      {isAdded ? (
                        <span className="ml-auto text-xs font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded">
                          Added
                        </span>
                      ) : isCustomVendorOption || isContractorOption ? (
                        <span className="ml-auto">
                          <Button color="tertiary" size="s" onClick={() => {}}>
                            Add
                          </Button>
                        </span>
                      ) : null}
                    </div>
                  )}
                </Combobox.Option>
              );
            })}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
}

interface SelectedVendorRowProps {
  vendor: SelectedVendor;
  onRemove: () => void;
}

function SelectedVendorRow({ vendor, onRemove }: SelectedVendorRowProps) {
  return (
    <div className="flex items-center justify-between py-2 px-3 border border-gray-200 rounded-md">
      <div className="flex items-center gap-3">
        {vendor.logoUrl ? (
          <img src={vendor.logoUrl} alt={vendor.name} className="h-6 w-6 flex-shrink-0" />
        ) : (
          <div className="h-6 w-6 rounded bg-gray-200 flex items-center justify-center">
            {vendor.isCustom ? (
              <PuzzlePieceIcon className="h-4 w-4 text-gray-500" />
            ) : (
              <span className="text-xs text-gray-500">{vendor.name.charAt(0).toUpperCase()}</span>
            )}
          </div>
        )}
        <span className="text-sm font-medium text-gray-900">{vendor.name}</span>
      </div>
      <Button color="tertiary" size="s" onClick={onRemove}>
        Remove
      </Button>
    </div>
  );
}

// interface CustomVendorOptionRowProps {
//   onAdd: () => void;
// }

// function CustomVendorOptionRow({ onAdd }: CustomVendorOptionRowProps) {
//   return (
//     <div
//       className="flex items-center justify-between py-2 px-3 cursor-pointer hover:bg-gray-100 border border-primary-500 rounded mx-1 my-1"
//       onClick={(e) => {
//         e.preventDefault();
//         e.stopPropagation();
//         onAdd();
//       }}
//     >
//       <div className="flex items-center gap-3">
//         <div className="h-6 w-6 rounded bg-gray-100 flex items-center justify-center">
//           <PuzzlePieceIcon className="h-4 w-4 text-gray-500" />
//         </div>
//         <span className="text-sm text-gray-900">Custom Vendor / Internal Data Storage</span>
//       </div>
//       <Button
//         color="tertiary"
//         size="s"
//         onClick={(e) => {
//           e.preventDefault();
//           e.stopPropagation();
//           onAdd();
//         }}
//       >
//         Add
//       </Button>
//     </div>
//   );
// }

type VendorDetailsModalType = "custom-vendor" | "contractor";

function VendorDetailsModal({
  type,
  initialVendorName,
  initialCustomVendorCategory,
  existingCategories,
  existingContractorNames = [],
  onBack,
  onCancel,
  onDone,
}: {
  type: VendorDetailsModalType;
  initialVendorName: string;
  initialCustomVendorCategory?: string;
  existingCategories: Set<string>;
  existingContractorNames?: string[];
  onBack: () => void;
  onCancel: () => void;
  onDone: (vendor: SelectedVendor) => void;
}) {
  const [vendorName, setVendorName] = useState(initialVendorName);
  const [isInternalStorage, setIsInternalStorage] = useState(false);
  const [category, setCategory] = useState<ComboFieldItem | null>(
    type === "custom-vendor" && initialCustomVendorCategory
      ? { id: initialCustomVendorCategory, name: initialCustomVendorCategory }
      : null,
  );
  const [isLoading] = useState(false);

  const isContractor = type === "contractor";

  const contractorNameError = isContractor
    ? validateNotIn(
        existingContractorNames,
        "A Contractor already exists with that name",
        false,
      )(vendorName.trim())
    : undefined;

  const handleDone = async () => {
    if (contractorNameError) {
      return;
    }
    const categoryName = category?.name ?? "";
    if (isContractor) {
      onDone({
        name: vendorName.trim(),
        category: categoryName,
        isNewVendor: true,
        newVendorType: "contractor",
      });
    } else {
      onDone({
        name: vendorName,
        isInternalStorage,
        category: isInternalStorage ? DATA_STORAGE_LOCATION_CATEGORY : categoryName,
        isNewVendor: true,
        newVendorType: isInternalStorage ? "storage_location" : "vendor",
      });
    }
  };

  const sortedCategoryItems: ComboFieldItem[] = useMemo(() => {
    if (isContractor) {
      const categories = Object.values(CONTRACTOR_CATEGORY);
      categories.sort((a, b) => a.localeCompare(b));
      return categories.map((cat) => ({ id: cat, name: cat }));
    }
    const categories = Array.from(existingCategories);
    categories.sort();
    return categories.map((cat) => ({ id: cat, name: cat }));
  }, [isContractor, existingCategories]);

  const modalTitle = isContractor
    ? "Contractor / Agency Details"
    : "Custom Vendor / Internal Data Storage Details";
  const nameLabel = isContractor ? "Contractor / Agency Name" : "Vendor Name";
  const namePlaceholder = isContractor ? "Enter contractor or agency name" : "Enter vendor name";
  const infoboxTitle = isContractor
    ? "Regarding contractor or agencies types:"
    : "Regarding third party recipients types such as: Co-Marketing Partner, Data Buyer, Business Partner, Affiliate";
  const infoboxBody = isContractor ? (
    <>
      <p>
        For any contractors your business works with that use or have access to consumer personal
        information select the applicable category above such as Accounting Contractor, etc.
      </p>
      <p>For marketing agencies running your ad campaigns, select category as Agency.</p>
    </>
  ) : (
    <>
      <p>
        In addition to your vendors, your business may disclose consumer data to other parties that
        may not provide an explicit service for your business. These may include co-marketing
        partners, data buyers, affiliates, investors, or business partners.
      </p>
      <p>If your custom vendor is one of these types, select the applicable category above.</p>
    </>
  );

  const showInternalStorageCheckbox = !isContractor;
  const showCategoryDropdown = isContractor || !isInternalStorage;

  return (
    <Modal
      showOverlay
      closeButton
      onClose={isLoading ? undefined : onCancel}
      maxPanelWidth="max-w-[600px]"
      contentContainerClasses="w-full"
      alignText="left"
      alignButton="right"
      // firstButton="Cancel"
      // firstButtonDisabled={isLoading}
      // onFirstButtonClick={onCancel}
      secondButton="Done"
      secondButtonColor="primary"
      secondButtonDisabled={
        !vendorName.trim() ||
        (!category && !isInternalStorage) ||
        Boolean(contractorNameError) ||
        isLoading
      }
      onSecondButtonClick={handleDone}
      title={
        <div>
          {/* Back link */}
          <button
            onClick={onBack}
            disabled={isLoading}
            className={clsx(
              "flex items-center gap-1 text-sm mb-4",
              isLoading ? "text-gray-400 cursor-not-allowed" : "text-gray-600 hover:text-gray-900",
            )}
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span>Back to Add New Vendor</span>
          </button>
          {/* Title */}
          <span>{modalTitle}</span>
        </div>
      }
    >
      <div className="relative">
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-50 z-10 flex items-center justify-center rounded-md">
            <CircularProgress size="3em" />
          </div>
        )}

        <div className="space-y-4">
          {/* Vendor Name Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{nameLabel}</label>
            <input
              type="text"
              className={clsx(
                "block w-full rounded-md border py-2 px-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-1",
                contractorNameError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-primary-500 focus:ring-primary-500",
              )}
              value={vendorName}
              onChange={(e) => setVendorName(e.target.value)}
              placeholder={namePlaceholder}
            />
            {contractorNameError && (
              <p className="mt-1 text-sm text-red-600">{contractorNameError}</p>
            )}
          </div>

          {/* Internal Storage Checkbox - only for custom vendors */}
          {showInternalStorageCheckbox && (
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="isInternalStorage"
                checked={isInternalStorage}
                onChange={(e) => setIsInternalStorage(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="isInternalStorage" className="text-sm text-gray-700">
                This is an{" "}
                <span className="font-medium">internal database or data storage location</span> (not
                an external vendor, contractor, or agency).
              </label>
            </div>
          )}

          {/* Category Dropdown */}
          {showCategoryDropdown && (
            <div>
              <ComboField
                items={sortedCategoryItems}
                value={category}
                onChange={setCategory}
                label="Category"
                placeholder="Select or type a category..."
                showOptionsOnFocus
              />
            </div>
          )}

          {showCategoryDropdown && (
            <div className="flex gap-3 rounded-md bg-purple-50 p-4">
              <div className="flex-shrink-0">
                <InformationCircleIcon className="h-5 w-5 text-purple-600" />
              </div>
              <p className="text-sm text-purple-700">
                It's okay if the category you select isn't a perfect fit. If you're torn between two
                categories, go with the one that is more specific.
              </p>
            </div>
          )}

          <div className="flex gap-3 p-4 bg-blue-50 rounded-md">
            <div className="flex-shrink-0">
              <InformationCircleIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div className="text-sm">
              <p className="text-blue-700 font-medium">{infoboxTitle}</p>
              <div className="text-blue-700 mt-1 space-y-[6px]">{infoboxBody}</div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// Wrapper components for backward compatibility
function CustomVendorDetailsModal(props: {
  initialVendorName: string;
  initialCategory?: string;
  existingCategories: Set<string>;
  onBack: () => void;
  onCancel: () => void;
  onDone: (vendor: SelectedVendor) => void;
}) {
  const { initialCategory, ...vendorDetailsProps } = props;
  return (
    <VendorDetailsModal
      {...vendorDetailsProps}
      type="custom-vendor"
      initialCustomVendorCategory={initialCategory}
    />
  );
}

function ContractorDetailsModal(props: {
  initialVendorName: string;
  existingCategories: Set<string>;
  existingContractorNames: string[];
  onBack: () => void;
  onCancel: () => void;
  onDone: (vendor: SelectedVendor) => void;
}) {
  return <VendorDetailsModal {...props} type="contractor" />;
}
